import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from '@mui/material';
import { Add, Edit, Delete, OpenInNew } from '@mui/icons-material';
import { noticiaService, Noticia, NoticiaForm } from '../services/noticiaService';
import RichTextEditor from '../components/RichTextEditor';

const emptyForm: NoticiaForm = {
  titulo: '',
  resumo: '',
  corpo: '',
  publicar: true,
};

const NoticiasSite: React.FC = () => {
  const [noticias, setNoticias] = useState<Noticia[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState<Noticia | null>(null);
  const [formData, setFormData] = useState<NoticiaForm>(emptyForm);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const siteBaseUrl = process.env.REACT_APP_SITE_URL || 'http://localhost:3001';

  useEffect(() => {
    loadNoticias();
  }, []);

  const loadNoticias = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await noticiaService.listar();
      setNoticias(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao carregar notícias');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setFormData(emptyForm);
    setOpenDialog(true);
  };

  const openEdit = (noticia: Noticia) => {
    setEditing(noticia);
    setFormData({
      titulo: noticia.titulo,
      resumo: noticia.resumo,
      corpo: noticia.corpo,
      publicar: noticia.status === 'publicado',
    });
    setOpenDialog(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await noticiaService.atualizar(editing.id, formData);
      } else {
        await noticiaService.criar(formData);
      }
      setOpenDialog(false);
      await loadNoticias();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Erro ao salvar notícia');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (noticia: Noticia) => {
    if (!window.confirm(`Excluir a notícia "${noticia.titulo}"?`)) return;
    try {
      await noticiaService.excluir(noticia.id);
      await loadNoticias();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao excluir notícia');
    }
  };

  const formatDate = (value?: string | null) => {
    if (!value) return '—';
    return new Date(value).toLocaleString('pt-BR');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Notícias do site
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Publicação no site público ({siteBaseUrl}/noticias). Somente administradores.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
          Nova notícia
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Título</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Publicado em</TableCell>
                <TableCell>Autor</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {noticias.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Nenhuma notícia cadastrada.
                  </TableCell>
                </TableRow>
              ) : (
                noticias.map((noticia) => (
                  <TableRow key={noticia.id}>
                    <TableCell>
                      <Typography fontWeight={600}>{noticia.titulo}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        /noticias/{noticia.slug}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={noticia.status === 'publicado' ? 'Publicado' : 'Rascunho'}
                        color={noticia.status === 'publicado' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{formatDate(noticia.publicado_em)}</TableCell>
                    <TableCell>{noticia.autor_nome}</TableCell>
                    <TableCell align="right">
                      {noticia.status === 'publicado' && (
                        <Tooltip title="Ver no site">
                          <IconButton
                            component="a"
                            href={`${siteBaseUrl}/noticias/${noticia.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <OpenInNew />
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton onClick={() => openEdit(noticia)}>
                        <Edit />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDelete(noticia)}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{editing ? 'Editar notícia' : 'Nova notícia'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Título"
              value={formData.titulo}
              onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Resumo (aparece na listagem)"
              value={formData.resumo}
              onChange={(e) => setFormData({ ...formData, resumo: e.target.value })}
              fullWidth
              multiline
              minRows={2}
              required
            />
            <RichTextEditor
              value={formData.corpo}
              onChange={(corpo) => setFormData({ ...formData, corpo })}
              minHeight={320}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.publicar}
                  onChange={(e) => setFormData({ ...formData, publicar: e.target.checked })}
                />
              }
              label="Publicar no site (visível para todos)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NoticiasSite;
